import * as THREE from 'three';
import type { Floor, FloorIdT, Npc, Product, Worker } from '@/content/schema';
import type { Appearance, BasketState, CartState, GroundItem } from '@/core/state';
import { npcSprite, loadModel, hasAsset, assetUrl } from '@/assets/assetManager';
import { box, disposeTree, glowSprite, litMat, makeLabel, makeMarker, FONT, OVERLAY_LAYER } from './builders';
import { isNewStyle } from '@/ui/artStyle';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { animateChibi, makeChibi, SKIN, type AnimMode } from './chibi';
import { buildFloor, type FloorRuntime } from './floorBuilder';
import { NavGrid } from './pathfind';

export interface Actor { id: string; kind: 'caseNpc' | 'patrol' | 'ambient' | 'worker'; group: THREE.Group; label: THREE.Sprite | null; caseId?: string; npcId?: string; path?: [number, number][]; speed?: number; mode: AnimMode; solid: boolean }

/** 3D 舞台：渲染、相機、目前樓層的場景物件與角色。不含遊戲規則。 */
/** v1.0 圍裙顏色：生食拋棄式（白透明）、熟食與煮飯區天藍色、前置與洗籃區灰黑色 */
export const APRON_COLOR = { disposable: '#e9f1f3', skyblue: '#7cc4ea', graydark: '#3b4145' } as const;

export class Stage {
  readonly renderer: THREE.WebGLRenderer | null;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(42, 1, 0.1, 160);
  rt!: FloorRuntime; nav!: NavGrid;
  player!: THREE.Group;
  playerCarry: THREE.Group = new THREE.Group();
  actors: Actor[] = [];
  carts = new Map<string, THREE.Group>();
  pickups = new Map<string, THREE.Sprite>();
  baskets = new Map<string, THREE.Group>();
  marker = makeMarker('!');
  targetRing: THREE.Mesh; visionRings = new THREE.Group(); tapRing: THREE.Mesh;
  guaiguai: THREE.Group | null = null;
  hotspotRings = new THREE.Group();
  /** v1.1 頭上的標示：「!」＝任務下一步要去的地方；「🎮」＝這台機器有作業可以做 */
  signs = new THREE.Group();
  wind: THREE.Points;
  dust: THREE.Points;
  private hemi = new THREE.HemisphereLight(0xdfe9ee, 0x2a343b, 1.15);
  private sun = new THREE.DirectionalLight(0xffffff, 0.9);
  private torch = new THREE.PointLight(0xfff1d0, 0, 4.5, 1.2);
  /** 暗處主角身邊的小光圈（地上一圈亮亮的） */
  private glow: THREE.Mesh;
  /** 手電筒：主角前面一束光 */
  private flash = new THREE.SpotLight(0xfff4d6, 0, 13, Math.PI / 7, 0.45, 1);
  private flashCone: THREE.Mesh;
  flashlight = false;
  private camOffset = new THREE.Vector3(0, 15.5, 12);
  /** v1.4 新版畫面：陰影、環境光、Bloom 發光＋ACES 色調（經典版都不開） */
  readonly fancy = isNewStyle();
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private shadowTick = 0;
  /** v1.4 畫質：2＝陰影＋發光、1＝只有陰影、0＝都不開（材質、色調還是新版）。太卡（平均每格 > 50 ms）會自動往下降一級；網址 ?fx=high 固定最高、?fx=low 固定最低 */
  fxLevel = 3;
  private fxLock: 'high' | 'low' | null = (() => { try { const v = new URLSearchParams(location.search).get('fx'); return v === 'high' || v === 'low' ? v : null; } catch { return null; } })();
  private fxWin = { t0: 0, n: 0 };
  private tmp = new THREE.Vector3();
  zoom = 1;
  dark = false;

  constructor(readonly canvas: HTMLCanvasElement) {
    let r: THREE.WebGLRenderer | null = null;
    try { r = new THREE.WebGLRenderer({ canvas, antialias: true }); } catch { r = null; }
    this.renderer = r;
    if (r) r.outputColorSpace = THREE.SRGBColorSpace;
    this.sun.position.set(6, 14, 9);
    this.scene.add(this.hemi, this.sun, this.torch, this.flash, this.flash.target);
    if (r && this.fancy) this.setupFancy(r);
    const gc = document.createElement('canvas'); gc.width = gc.height = 128; const gx = gc.getContext('2d')!;
    const grd = gx.createRadialGradient(64, 64, 4, 64, 64, 64); grd.addColorStop(0, 'rgba(255,240,205,0.55)'); grd.addColorStop(0.6, 'rgba(255,240,205,0.18)'); grd.addColorStop(1, 'rgba(255,240,205,0)');
    gx.fillStyle = grd; gx.fillRect(0, 0, 128, 128);
    this.glow = new THREE.Mesh(new THREE.CircleGeometry(2.2, 40), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(gc), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    this.glow.rotation.x = -Math.PI / 2; this.glow.visible = false;
    const cc = document.createElement('canvas'); cc.width = 64; cc.height = 128; const cx = cc.getContext('2d')!;
    const cg = cx.createLinearGradient(0, 128, 0, 0); cg.addColorStop(0, 'rgba(255,244,214,0.5)'); cg.addColorStop(1, 'rgba(255,244,214,0)');
    cx.fillStyle = cg; cx.beginPath(); cx.moveTo(32, 128); cx.lineTo(0, 0); cx.lineTo(64, 0); cx.closePath(); cx.fill();
    this.flashCone = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 6.5), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(cc), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
    this.flashCone.rotation.x = -Math.PI / 2; this.flashCone.visible = false;
    this.targetRing = new THREE.Mesh(new THREE.RingGeometry(0.85, 1.05, 40), new THREE.MeshBasicMaterial({ color: 0xf2b134, transparent: true, opacity: 0.85, depthWrite: false }));
    this.targetRing.rotation.x = -Math.PI / 2;
    this.tapRing = new THREE.Mesh(new THREE.RingGeometry(0.16, 0.26, 28), new THREE.MeshBasicMaterial({ color: 0xf2b134, transparent: true, opacity: 0, depthWrite: false }));
    this.tapRing.rotation.x = -Math.PI / 2;
    // 氣浴室的風
    const wg = new THREE.BufferGeometry(); const wp = new Float32Array(120 * 3);
    for (let i = 0; i < 120; i++) { wp[i * 3] = (Math.random() - 0.5) * 1.6; wp[i * 3 + 1] = Math.random() * 2.2; wp[i * 3 + 2] = (Math.random() - 0.5) * 1.6; }
    wg.setAttribute('position', new THREE.BufferAttribute(wp, 3));
    this.wind = new THREE.Points(wg, new THREE.PointsMaterial({ color: 0xcfefff, size: 0.06, transparent: true, opacity: 0.85 }));
    this.wind.visible = false;
    // 灰塵（5F）
    const dg = new THREE.BufferGeometry(); const dp = new Float32Array(300 * 3);
    for (let i = 0; i < 300; i++) { dp[i * 3] = (Math.random() - 0.5) * 14; dp[i * 3 + 1] = Math.random() * 2.6; dp[i * 3 + 2] = (Math.random() - 0.5) * 10; }
    dg.setAttribute('position', new THREE.BufferAttribute(dp, 3));
    this.dust = new THREE.Points(dg, new THREE.PointsMaterial({ color: 0xd8cfb5, size: 0.035, transparent: true, opacity: 0.6 }));
    this.dust.visible = false;
  }

  makePlayer(look: Appearance, uniform: Parameters<typeof makeChibi>[0]['uniform'], hat: Parameters<typeof makeChibi>[0]['hat'], dressed: boolean, apron?: string): void {
    const old = this.player;
    // 有 Gemini 做的主角站立圖就用圖（characters/player/sprite_<制服>.webp），沒有就用程式生成的 Q 版小人
    const spr = npcSprite(`characters/player/sprite_${dressed ? uniform : 'street'}.webp`);
    let p: THREE.Group;
    if (spr) { p = new THREE.Group(); p.add(spr); }
    else p = makeChibi({ uniform: dressed ? uniform : 'street', hat: dressed ? hat : 'none', height: look.height, skin: SKIN[look.skin] ?? SKIN[0], face: look.face, shirt: look.shirt, hair: look.hair, apron: dressed ? apron : undefined });
    if (old) { p.position.copy(old.position); p.rotation.copy(old.rotation); this.scene.remove(old); disposeTree(old); }
    p.add(this.playerCarry);
    this.player = p; this.scene.add(p);
  }

  loadFloor(floor: Floor, pos: [number, number], flags: Record<string, boolean>, lightsOn: boolean, doorOk: ConstructorParameters<typeof NavGrid>[1]): void {
    if (this.rt) { this.scene.remove(this.rt.group); disposeTree(this.rt.group); }
    for (const a of this.actors) { this.scene.remove(a.group); if (a.label) this.scene.remove(a.label); }
    this.actors = [];
    this.rt = buildFloor(floor, { flags });
    this.nav = new NavGrid(this.rt, doorOk);
    this.scene.add(this.rt.group);
    this.setDark(!!floor.dark && !lightsOn, floor);
    if (this.player) this.player.position.set(pos[0], 0, pos[1]);
    this.scene.add(this.marker, this.targetRing, this.visionRings, this.tapRing, this.hotspotRings, this.signs, this.wind, this.dust, this.glow, this.flashCone, this.trail);
    this.baskets.forEach(b => this.scene.remove(b)); this.baskets.clear();
    for (const e of this.rt.equipment.values()) if (e.data.model) {
      loadModel(e.data.model).then(m => { if (!m || !this.rt.equipment.has(e.data.id)) return; e.group.clear(); m.traverse(o => { o.userData.kind = 'equipment'; o.userData.id = e.data.id; }); e.group.add(m); });
    }
    this.snapCamera();
    if (this.fancy) this.settle();
  }

  setDark(dark: boolean, floor: Floor): void {
    this.dark = dark;
    this.scene.background = new THREE.Color(dark ? '#050506' : floor.theme.bg);
    this.scene.fog = dark ? new THREE.Fog('#050506', 14, 22) : new THREE.Fog(floor.theme.bg, 30, 70);
    const fk = this.fancy ? { hemi: 0.3, sun: 2.3 } : { hemi: 1, sun: 1 };
    this.hemi.intensity = (dark ? 0.12 : floor.dark ? 0.7 : 1.15) * fk.hemi;
    this.sun.intensity = (dark ? 0.02 : floor.dark ? 0.5 : 0.9) * fk.sun;
    if (this.fancy) this.scene.environmentIntensity = dark ? 0.04 : floor.dark ? 0.14 : 0.22;
    this.torch.intensity = dark ? 6 : 0;
    this.glow.visible = dark;
    this.dust.visible = !!floor.dark;
  }

  addWorker(w: Worker, i: number): Actor {
    const hatRing = w.hat;
    const variant = w.apron === APRON_COLOR.disposable ? `${w.uniform}_raw` : w.apron === APRON_COLOR.skyblue ? `${w.uniform}_cooked` : w.apron === APRON_COLOR.graydark ? `${w.uniform}_prep` : w.uniform;
    const spr = npcSprite(`characters/worker/sprite_${variant}.webp`) ?? npcSprite(`characters/worker/sprite_${w.uniform}.webp`);
    let g: THREE.Group;
    if (spr) { g = new THREE.Group(); g.add(spr); }
    else g = makeChibi({ uniform: w.uniform, hat: hatRing, height: (['short', 'mid', 'tall'] as const)[i % 3], skin: SKIN[i % SKIN.length], face: (['smile', 'calm', 'determined', 'sleepy'] as const)[i % 4], apron: w.apron, gloves: w.gloves });
    g.position.set(w.pos[0], 0, w.pos[1]); g.rotation.y = (w.facing * Math.PI) / 180 + Math.PI;
    if (w.mode === 'seated') g.position.y = 0.05;
    g.traverse(x => { x.userData.kind = 'worker'; });
    this.scene.add(g);
    let label: THREE.Sprite | null = null;
    if (w.tag) { label = makeLabel(w.tag, undefined, { scale: 0.62, bg: w.apron === APRON_COLOR.skyblue ? 'rgba(63,120,168,0.9)' : 'rgba(80,90,96,0.9)' }); label.position.set(w.pos[0], 2.3, w.pos[1]); this.scene.add(label); }
    const a: Actor = { id: `w${i}`, kind: 'worker', group: g, label, mode: w.mode, solid: true };
    this.actors.push(a); return a;
  }

  addNpc(o: { id: string; kind: Actor['kind']; npc: Npc; sub?: string; pos: [number, number]; caseId?: string }): Actor {
    const n = o.npc;
    let g: THREE.Group;
    const spr = npcSprite(n.sprite);
    if (spr) { g = new THREE.Group(); g.add(spr); }
    else g = makeChibi({ uniform: n.uniform, hat: n.hat, height: n.height, apron: n.color, skin: SKIN[n.id.length % SKIN.length], face: 'smile' });
    if (n.model) loadModel(n.model).then(m => { if (m) { g.clear(); g.add(m); } });
    g.position.set(o.pos[0], 0, o.pos[1]);
    g.traverse(x => { x.userData.kind = 'actor'; x.userData.id = o.id; });
    const label = makeLabel(n.shortName, o.sub ?? n.title, { scale: 0.78 }); label.position.set(o.pos[0], 2.3, o.pos[1]);
    this.scene.add(g, label);
    const a: Actor = { id: o.id, kind: o.kind, group: g, label, caseId: o.caseId, npcId: n.id, mode: 'idle', solid: true };
    this.actors.push(a); return a;
  }
  removeActors(filter: (a: Actor) => boolean): void {
    for (const a of this.actors.filter(filter)) { this.scene.remove(a.group); if (a.label) this.scene.remove(a.label); }
    this.actors = this.actors.filter(a => !filter(a));
  }

  /** 烏龜車（含籃子堆疊） */
  syncCarts(carts: Record<string, CartState>, floor: FloorIdT, pushing: string | null, basketColor: (id: string) => string = () => '#4f8fd0', holdingCart: string | null = null): void {
    for (const [id, g] of this.carts) { if (!carts[id] || carts[id].floor !== floor) { this.scene.remove(g); this.carts.delete(id); } }
    for (const [id, c] of Object.entries(carts)) {
      if (c.floor !== floor || id === holdingCart) { const g0 = this.carts.get(id); if (g0) { this.scene.remove(g0); this.carts.delete(id); } continue; }
      let g = this.carts.get(id);
      const key = `${c.load?.baskets ?? 0}|${c.load?.material ?? ''}|${c.baskets.map(b => b + basketColor(b)).join(',')}`;
      if (!g || g.userData.key !== key) {
        if (g) this.scene.remove(g);
        g = new THREE.Group(); g.userData.key = key;
        const base = new THREE.Mesh(box(1.1, 0.12, 0.8), litMat({ color: '#3f78a8' })); base.position.y = 0.16; g.add(base);
        for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) { const wh = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.06, 10), litMat({ color: '#222' })); wh.rotation.z = Math.PI / 2; wh.position.set(sx * 0.45, 0.07, sz * 0.3); g.add(wh); }
        const n = c.load?.baskets ?? 0;
        for (let i = 0; i < n; i++) {
          const b = new THREE.Mesh(box(0.9, 0.24, 0.62), litMat({ color: i === n - 1 ? '#2f6fb3' : '#4f8fd0' }));
          b.position.y = 0.36 + i * 0.25; g.add(b);
        }
        if (n) { const lid = new THREE.Mesh(box(0.92, 0.04, 0.64), litMat({ color: '#1f4f8a' })); lid.position.y = 0.36 + n * 0.25 - 0.1; g.add(lid); }
        c.baskets.forEach((bid, j) => { const b = makeBasket(basketColor(bid)); b.position.set(0, 0.24 + (n + j) * 0.32, 0); g!.add(b); });
        g.traverse(x => { x.userData.kind = 'cart'; x.userData.id = id; });
        this.scene.add(g); this.carts.set(id, g);
      }
      if (id !== pushing) { g.position.set(c.pos[0], 0, c.pos[1]); }
    }
  }

  /** 地上的小圖示（有圖用圖，沒圖用 emoji） */
  syncPickups(items: GroundItem[], floor: FloorIdT, icon: (type: string) => { emoji: string; image: string }): void {
    const want = new Set(items.filter(i => i.floor === floor).map(i => i.uid));
    for (const [uid, s] of this.pickups) if (!want.has(uid)) { this.scene.remove(s); this.pickups.delete(uid); }
    for (const it of items) {
      if (it.floor !== floor || this.pickups.has(it.uid)) continue;
      const ic = icon(it.type);
      let tex: THREE.Texture;
      if (hasAsset(ic.image)) { tex = new THREE.TextureLoader().load(assetUrl(ic.image)); }
      else {
        const cv = document.createElement('canvas'); cv.width = cv.height = 96; const x = cv.getContext('2d')!;
        x.fillStyle = 'rgba(255,255,255,0.88)'; x.beginPath(); x.arc(48, 48, 40, 0, Math.PI * 2); x.fill();
        x.strokeStyle = '#f2b134'; x.lineWidth = 6; x.stroke();
        x.font = `52px ${FONT}`; x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillText(ic.emoji, 48, 52);
        tex = new THREE.CanvasTexture(cv);
      }
      tex.colorSpace = THREE.SRGBColorSpace;
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true }));
      s.scale.set(0.55, 0.55, 1); s.position.set(it.pos[0], 0.4, it.pos[1]);
      s.userData.kind = 'pickup'; s.userData.id = it.uid;
      this.scene.add(s); this.pickups.set(it.uid, s);
    }
  }

  /** 地上（墊底籃架、線邊、冰箱）的籃子；v0.5：疊在上面的籃子（on='bk:<id>'）一起往上畫 */
  syncBaskets(baskets: Record<string, BasketState>, floor: FloorIdT, color: (content: string) => string): void {
    const above = new Map<string, string>();
    for (const [id, b] of Object.entries(baskets)) if (b.on?.startsWith('bk:')) above.set(b.on.slice(3), id);
    const chain = (id: string) => { const out = [id]; let cur = above.get(id); while (cur && out.length < 12) { out.push(cur); cur = above.get(cur); } return out; };
    const keyOf = (id: string, b: BasketState) => `${b.pos.join(',')}|${b.y ?? 0}|${chain(id).map(x => `${x}${baskets[x].used ? '*' : ''}${baskets[x].content}`).join(',')}`;
    for (const [id, g] of this.baskets) { const b = baskets[id]; if (!b || b.floor !== floor || b.on !== null || g.userData.key !== keyOf(id, b)) { this.scene.remove(g); this.baskets.delete(id); } }
    for (const [id, b] of Object.entries(baskets)) {
      if (b.floor !== floor || b.on !== null || this.baskets.has(id)) continue;
      const g = new THREE.Group();
      chain(id).forEach((bid, i) => { const one = makeBasket(color(baskets[bid].content), baskets[bid].used); one.position.y = i * 0.3; g.add(one); });
      g.position.set(b.pos[0], b.y ?? 0, b.pos[1]); g.userData.key = keyOf(id, b);
      g.traverse(o => { o.userData.kind = 'basket'; o.userData.id = id; });
      this.scene.add(g); this.baskets.set(id, g);
    }
  }
  /** 手上拿的東西（籃子可以疊好幾籃、拿起來的烏龜車）顯示在主角身前 */
  setHolding(kind: 'basket' | 'cart' | null, colors: string | string[] = '#4f8fd0'): void {
    this.playerCarry.clear();
    if (kind === 'basket') (Array.isArray(colors) ? colors : [colors]).forEach((c, i) => { const b = makeBasket(c); b.position.set(0, 0.62 + i * 0.26, 0.42); b.scale.setScalar(0.85); this.playerCarry.add(b); });
    if (kind === 'cart') {
      const base = new THREE.Mesh(box(1.0, 0.1, 0.7), litMat({ color: '#3f78a8' }));
      base.position.set(0, 1.0, 0.45); base.rotation.x = -1.2; this.playerCarry.add(base);
    }
  }
  /** 導航：地上一串黃色小點，帶玩家走到任務目標 */
  readonly trail = new THREE.Group();
  setTrail(points: [number, number][]): void {
    while (this.trail.children.length < points.length) {
      const m = new THREE.Mesh(new THREE.CircleGeometry(0.11, 12), new THREE.MeshBasicMaterial({ color: 0xf2c84b, transparent: true, opacity: 0.85, depthWrite: false }));
      m.rotation.x = -Math.PI / 2; this.trail.add(m);
    }
    this.trail.children.forEach((m, i) => { m.visible = i < points.length; if (m.visible) m.position.set(points[i][0], 0.04, points[i][1]); });
  }

  /** 輸送帶上的成品／半成品持續移動；換線時改顏色 */
  animateConveyors(t: number, productFor: (line?: string) => { product: Product | null; alt: boolean; stopped: boolean }): void {
    for (const cv of this.rt.conveyors) {
      const info = productFor(cv.line);
      const color = cv.line ? (info.product ? (info.alt ? info.product.alt.color : info.product.color) : '#cccccc') : ({ rice: '#f7f3e8', veg: '#7fbf6a', chicken: '#c8873f', fried: '#e2a84a' } as Record<string, string>)[cv.product ?? ''] ?? '#ddd';
      const speed = info.stopped ? 0 : cv.line ? 0.55 : 0.4;
      const n = cv.items.length;
      cv.items.forEach((m, i) => {
        const off = ((t * speed + i * cv.spacing) % (n * cv.spacing)) - (n * cv.spacing) / 2;
        if (cv.axis === 'x') m.position.x = cv.eq.pos[0] + off; else m.position.z = cv.eq.pos[1] + off;
        const mm = m.material as THREE.MeshLambertMaterial;
        if (mm.userData.c !== color) { mm.color.set(color); mm.userData.c = color; }
        m.visible = !(info.stopped && i % 2);
      });
    }
  }

  /** v0.6 老鼠：灰色小身體＋粉紅耳朵＋長尾巴 */
  private rat: THREE.Group | null = null;
  setRat(pos: [number, number] | null): void {
    if (this.rat) { this.scene.remove(this.rat); this.rat = null; }
    if (!pos) return;
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.16, 14, 10), litMat({ color: '#7d7f84' })); body.scale.set(1, 0.75, 1.5); body.position.y = 0.13; g.add(body);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.09, 12, 8), litMat({ color: '#8a8c91' })); head.position.set(0, 0.15, 0.24); g.add(head);
    for (const sx of [-1, 1]) { const ear = new THREE.Mesh(new THREE.CircleGeometry(0.045, 10), litMat({ color: '#f2a7b5', side: THREE.DoubleSide })); ear.position.set(sx * 0.06, 0.24, 0.22); g.add(ear); }
    const nose = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 6), litMat({ color: '#e07b8a' })); nose.position.set(0, 0.15, 0.33); g.add(nose);
    const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.02, 0.4, 6), litMat({ color: '#d98f9c' })); tail.rotation.x = Math.PI / 2.4; tail.position.set(0, 0.08, -0.36); g.add(tail);
    g.position.set(pos[0], 0, pos[1]);
    g.traverse(o => { o.userData.kind = 'rat'; o.userData.id = 'rat'; });
    this.scene.add(g); this.rat = g;
  }
  moveRat(pos: [number, number], heading: number): void {
    if (!this.rat) return;
    this.rat.position.set(pos[0], Math.abs(Math.sin(performance.now() / 60)) * 0.03, pos[1]); this.rat.rotation.y = heading;
  }
  setGuaiguai(pos: [number, number, number] | null): void {
    if (this.guaiguai) { this.scene.remove(this.guaiguai); this.guaiguai = null; }
    if (!pos) return;
    const g = new THREE.Group();
    const bag = new THREE.Mesh(box(0.34, 0.42, 0.12), litMat({ color: 0x3fae49, emissive: 0x0d3311 })); bag.position.y = 0.21; g.add(bag);
    const band = new THREE.Mesh(box(0.35, 0.08, 0.125), litMat({ color: 0xf2d33c })); band.position.y = 0.3; g.add(band);
    g.position.set(pos[0], pos[1], pos[2]); this.guaiguai = g; this.scene.add(g);
  }
  private signTex = new Map<string, THREE.Texture>();
  private signTexture(kind: 'goal' | 'job'): THREE.Texture {
    let t = this.signTex.get(kind); if (t) return t;
    const cv = document.createElement('canvas'); cv.width = cv.height = 128; const x = cv.getContext('2d')!;
    x.beginPath(); x.arc(64, 64, 56, 0, Math.PI * 2);
    x.fillStyle = kind === 'goal' ? '#f2c84b' : '#3f9e6c'; x.fill(); x.lineWidth = 8; x.strokeStyle = '#1a2630'; x.stroke();
    x.fillStyle = kind === 'goal' ? '#1a2630' : '#ffffff'; x.textAlign = 'center'; x.textBaseline = 'middle';
    x.font = kind === 'goal' ? '900 92px sans-serif' : '700 64px sans-serif'; x.fillText(kind === 'goal' ? '!' : '🎮', 64, 70);
    t = new THREE.CanvasTexture(cv); this.signTex.set(kind, t); return t;
  }
  /** v1.1 設定頭上標示（每次整批換） */
  setSigns(list: { x: number; z: number; y: number; kind: 'goal' | 'job' }[]): void {
    for (const c of [...this.signs.children]) { this.signs.remove(c); ((c as THREE.Sprite).material as THREE.Material).dispose(); }
    for (const p of list) {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.signTexture(p.kind), depthTest: false, transparent: true }));
      const sc = p.kind === 'goal' ? 0.9 : 0.55; sp.scale.set(sc, sc, 1); sp.position.set(p.x, p.y, p.z); sp.renderOrder = 20;
      glowSprite(sp, p.kind === 'goal' ? 1.7 : 1.1);
      sp.userData = { baseY: p.y, kind: p.kind }; this.signs.add(sp);
    }
  }
  setHotspots(points: [number, number][]): void {
    this.hotspotRings.clear();
    for (const [x, z] of points) {
      const m = new THREE.Mesh(new THREE.RingGeometry(1.2, 1.35, 32), new THREE.MeshBasicMaterial({ color: 0xd9534a, transparent: true, opacity: 0.6, depthWrite: false }));
      m.rotation.x = -Math.PI / 2; m.position.set(x, 0.035, z); this.hotspotRings.add(m);
    }
  }
  setVision(points: [number, number][], r: number): void {
    while (this.visionRings.children.length < points.length) {
      const m = new THREE.Mesh(new THREE.CircleGeometry(1, 48), new THREE.MeshBasicMaterial({ color: 0xe06c75, transparent: true, opacity: 0.11, depthWrite: false }));
      m.rotation.x = -Math.PI / 2; this.visionRings.add(m);
    }
    this.visionRings.children.forEach((m, i) => { m.visible = i < points.length; if (m.visible) { m.position.set(points[i][0], 0.025, points[i][1]); m.scale.set(r, r, 1); } });
  }

  updateEffects(t: number, airshower: boolean): void {
    for (const c of this.signs.children) if (c.userData.kind === 'goal') c.position.y = c.userData.baseY + Math.sin(t * 3) * 0.15;
    const pp = this.player.position;
    this.torch.position.set(pp.x, 1.6, pp.z + 0.2);
    this.glow.position.set(pp.x, 0.03, pp.z);
    const on = this.dark && this.flashlight;
    this.flash.intensity = on ? 60 : 0; this.flashCone.visible = on;
    if (on) {
      const ry = this.player.rotation.y, fx = Math.sin(ry), fz = Math.cos(ry);
      this.flash.position.set(pp.x + fx * 0.3, 1.2, pp.z + fz * 0.3);
      this.flash.target.position.set(pp.x + fx * 6, 0, pp.z + fz * 6);
      this.flashCone.position.set(pp.x + fx * 3.6, 0.035, pp.z + fz * 3.6);
      this.flashCone.rotation.z = ry + Math.PI;
    }
    for (const m of this.trail.children) (((m as THREE.Mesh).material) as THREE.MeshBasicMaterial).opacity = 0.55 + Math.sin(t * 5 + m.position.x + m.position.z) * 0.3;
    this.wind.visible = airshower;
    if (airshower) {
      this.wind.position.copy(this.player.position);
      const a = this.wind.geometry.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < a.count; i++) {
        let x = a.getX(i), z = a.getZ(i);
        const ang = 0.25, r = Math.hypot(x, z) || 0.5;
        const th = Math.atan2(z, x) + ang;
        x = Math.cos(th) * Math.max(0.2, r * 0.985); z = Math.sin(th) * Math.max(0.2, r * 0.985);
        if (r < 0.25) { x = (Math.random() - 0.5) * 1.6; z = (Math.random() - 0.5) * 1.6; }
        a.setXYZ(i, x, (a.getY(i) + 0.03) % 2.2, z);
      }
      a.needsUpdate = true;
    }
    if (this.dust.visible) this.dust.rotation.y = t * 0.01;
  }

  get floorId(): FloorIdT { return this.rt.floor.id; }
  desiredCam(): THREE.Vector3 {
    const f = (this.camera.aspect < 0.8 ? 1.5 : 1) * this.zoom * (this.rt?.floor.id === '5F' ? 0.7 : 1);
    return this.tmp.copy(this.camOffset).multiplyScalar(f).add(this.player.position);
  }
  snapCamera(): void { if (!this.player) return; this.camera.position.copy(this.desiredCam()); this.camera.lookAt(this.player.position.x, 0.8, this.player.position.z); }
  followCamera(dt: number): void {
    this.camera.position.lerp(this.desiredCam(), 1 - Math.exp(-dt * 4));
    // 暗的樓層：霧從「相機到主角的距離」開始，主角身邊看得到、遠處漸漸黑掉
    if (this.dark && this.scene.fog instanceof THREE.Fog) { const dd = this.camera.position.distanceTo(this.player.position); this.scene.fog.near = dd - (this.flashlight ? 1.5 : 2.6); this.scene.fog.far = dd + (this.flashlight ? 9 : 2.4); }
    this.camera.lookAt(this.player.position.x, 0.8, this.player.position.z);
  }
  private size = { w: 1, h: 1 };
  resize(w: number, h: number): void {
    this.size = { w, h };
    // v1.4.1 新版：解析度上限 1.5 倍（Retina 2 倍太吃效能），太卡再降到 1 倍；經典版維持原本的 2 倍
    const pr = Math.min(window.devicePixelRatio || 1, this.fancy ? (this.fxLevel >= 3 ? 1.5 : 1) : 2);
    if (this.renderer) { this.renderer.setPixelRatio(pr); this.renderer.setSize(w, h, false); }
    if (this.composer && this.renderer) { this.composer.setPixelRatio(pr); this.composer.setSize(w, h); }
    this.camera.aspect = w / Math.max(h, 1); this.camera.updateProjectionMatrix();
  }
  private frame = 0;
  private startEl: HTMLElement | null = null;
  render(): void {
    const r = this.renderer; if (!r) return;
    if (!this.composer) { r.render(this.scene, this.camera); return; }
    // 作業視窗、開始畫面（新版開始畫面是一張場景截圖，整個蓋住）打開時，後面的 3D 停在最後一格、不重畫（省電、手機不燙）
    if (typeof document !== 'undefined') {
      this.startEl ??= document.getElementById('start');
      const covered = !!document.querySelector('.ww') || (!!this.startEl && !this.startEl.hidden);
      if (covered && this.frame > 2) { this.fxWin.t0 = 0; return; }
    }
    this.frame++;
    this.watchFps();
    // v1.4 新版：太陽跟著主角（陰影只算畫面附近）；陰影每 2 格更新一次（看不出差別、省一半）
    const c = this.player?.position;
    if (c) { this.sun.position.set(c.x - 9, 15, c.z + 2); this.sun.target.position.set(c.x, 0, c.z); this.sun.target.updateMatrixWorld(); }
    const now = performance.now(); if (now - this.shadowTick > 500) { this.shadowTick = now; this.shadowify(); }
    r.shadowMap.autoUpdate = false; r.shadowMap.needsUpdate = this.frame % 2 === 1;
    if (this.fxLevel <= 0) {   // 最低畫質：名牌一起畫（少一次整個場景的繪製）
      this.camera.layers.set(0); this.camera.layers.enable(OVERLAY_LAYER); r.render(this.scene, this.camera); this.camera.layers.set(0); return;
    }
    this.camera.layers.set(0);
    if (this.fxLevel >= 2) this.composer.render(); else r.render(this.scene, this.camera);
    // 名牌（第 1 層）畫在後製之後：不被色調、發光影響
    const auto = r.autoClear; r.autoClear = false; r.shadowMap.needsUpdate = false;
    const bg = this.scene.background; this.scene.background = null;   // 背景色會強制清畫面，第 1 層先拿掉
    this.camera.layers.set(OVERLAY_LAYER); r.render(this.scene, this.camera);
    this.camera.layers.set(0); this.scene.background = bg; r.autoClear = auto;
  }
  /** v1.4.1 每 1.5 秒看一次平均每格時間：低於約 40 fps（> 25 ms）就降一級（解析度 → 發光 → 陰影）。剛換樓層的前 2.5 秒不算（著色器編譯） */
  private settleUntil = 0;
  private watchFps(): void {
    if (this.fxLock === 'high' || this.fxLevel <= 0) return;
    const now = performance.now(), w = this.fxWin;
    if (now < this.settleUntil) { w.t0 = 0; return; }
    if (!w.t0) { w.t0 = now; w.n = 0; return; }
    w.n++;
    if (now - w.t0 < 1500) return;
    const avg = (now - w.t0) / Math.max(1, w.n); w.t0 = now; w.n = 0;
    if (avg > 25 && (typeof document === 'undefined' || document.visibilityState === 'visible')) { this.setFx(this.fxLevel - 1); this.settleUntil = now + 1000; }
  }
  /** 畫質：3＝1.5 倍解析度＋發光＋陰影、2＝1 倍解析度＋發光＋陰影、1＝只有陰影、0＝都不開 */
  setFx(level: number): void {
    const was = this.fxLevel;
    this.fxLevel = Math.max(0, Math.min(3, level));
    this.sun.castShadow = this.fxLevel >= 1;
    if ((was >= 3) !== (this.fxLevel >= 3)) this.resize(this.size.w, this.size.h);
  }
  /** 換樓層後：先把著色器編譯好，前 2.5 秒不判斷卡不卡 */
  settle(): void {
    this.settleUntil = performance.now() + 2500; this.frame = 0;
    try { this.renderer?.compile(this.scene, this.camera); } catch { /* ignore */ }
  }
  /** v1.4 新版畫面設定（只在新版呼叫一次） */
  private setupFancy(r: THREE.WebGLRenderer): void {
    const touch = typeof matchMedia !== 'undefined' && matchMedia('(pointer:coarse)').matches;
    r.toneMapping = THREE.ACESFilmicToneMapping; r.toneMappingExposure = 0.95;
    r.shadowMap.enabled = true; r.shadowMap.type = THREE.PCFShadowMap; r.shadowMap.autoUpdate = false;
    const sz = touch ? 1024 : 2048;
    this.sun.castShadow = true; this.sun.shadow.mapSize.set(sz, sz);
    const sc = this.sun.shadow.camera; sc.left = -15; sc.right = 15; sc.top = 15; sc.bottom = -15; sc.near = 1; sc.far = 45; sc.updateProjectionMatrix();
    this.sun.shadow.bias = -0.0006; this.sun.shadow.normalBias = 0.03; this.sun.shadow.radius = 3;
    this.scene.add(this.sun.target);
    try {
      const pm = new THREE.PMREMGenerator(r);
      this.scene.environment = pm.fromScene(new RoomEnvironment(), 0.04).texture; pm.dispose();
    } catch { /* 沒有也可以 */ }
    const composer = new EffectComposer(r);
    composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), 0.5, 0.45, 1.25);
    composer.addPass(this.bloom);
    composer.addPass(new OutputPass());
    this.composer = composer;
    this.fxLevel = touch ? 2 : 3;   // 手機一開始就用 1 倍解析度
    if (this.fxLock === 'low') this.setFx(0);
    if (this.fxLock === 'high') this.fxLevel = 3;
  }
  /** 會受光的實心物件：投影＋接受陰影（透明、發光貼圖、平面字、太小的東西不投影——小東西的影子看不出來，卻很花時間） */
  private shadowify(): void {
    const sph = new THREE.Sphere(), sc = new THREE.Vector3();
    this.scene.traverse(o => {
      const m = o as THREE.Mesh; if (!m.isMesh || o.userData.shadowSet) return;
      o.userData.shadowSet = true;
      const mt = m.material as THREE.Material;
      if (!mt || (mt as THREE.MeshBasicMaterial).isMeshBasicMaterial || mt.transparent) return;
      const g = m.geometry as THREE.BufferGeometry & { type: string };
      const flat = g.type === 'PlaneGeometry' || g.type === 'CircleGeometry' || g.type === 'RingGeometry';
      m.receiveShadow = true;
      if (flat || o.userData.noShadow) return;
      if (!g.boundingSphere) g.computeBoundingSphere();
      sph.copy(g.boundingSphere!); m.getWorldScale(sc);
      m.castShadow = sph.radius * Math.max(sc.x, sc.y, sc.z) >= 0.16;
    });
  }
  animateActor(a: Actor, t: number, reduce: boolean): void { animateChibi(a.group, t, a.mode, reduce); }
}

/** 周轉籃：藍色籃子＋裡面的東西（顏色）；used＝已經送到、放在線邊用 */
export function makeBasket(contentColor: string, used = false): THREE.Group {
  const g = new THREE.Group();
  // v0.8：髒籃（#7d6b4f）籃子顏色暗一點；空籃（#e6f0f4）只看得到籃底
  const dirty = contentColor === '#7d6b4f', empty = contentColor === '#e6f0f4';
  const crate = new THREE.Mesh(box(0.56, 0.3, 0.42), litMat({ color: dirty ? '#4d5f6e' : used ? '#6f8fb3' : '#2f6fb3' }));
  crate.position.y = 0.15; g.add(crate);
  const stuff = new THREE.Mesh(box(0.48, empty ? 0.02 : 0.08, 0.34), litMat({ color: contentColor }));
  stuff.position.y = empty ? 0.04 : 0.29; g.add(stuff);
  for (const sx of [-1, 1]) { const hole = new THREE.Mesh(box(0.02, 0.06, 0.16), litMat({ color: '#1f4f8a' })); hole.position.set(sx * 0.285, 0.22, 0); g.add(hole); }
  return g;
}
